import React, { Fragment, ReactNode, createContext, forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import BadgeModal from "./BadgeModal";
import { useGetPopUpQuery, useHasSeenPopUpMutation } from "api";
import RunningStrick from "components/BadgeSystem/RunningStrick";
import MissedStrick from "components/BadgeSystem/MissedStrick";
import StrickComplete from "components/BadgeSystem/StrickComplete";
import ReactGA from "react-ga4";
import { BADGE_STREAK_CLICKED } from "utills/events";
export interface IBadgeContext {
  handleOpenModal: (achievementType: string) => () => void;
}

export const BadgeContext = createContext<IBadgeContext>({} as IBadgeContext);

const popupModalView: Record<string, { fullScreen: boolean }> = {
  "PowerUserStreak": {
    fullScreen: false
  },
  "PowerUser_WeekCompleted": {
    fullScreen: false
  },
  "PowerUser_WeekMissed": {
    fullScreen: false
  },
  "PowerUser_StreakCompleted": {
    fullScreen: true
  }
}

export type BadgeHandle = {
  refetch: () => void;
};

type Props = {
  children: ReactNode;
}

export const BadgeProvider = forwardRef<BadgeHandle, Props>(({ children }, ref) => {
  const [open, setOpen] = useState(false);
  const { data, refetch, loading } = useGetPopUpQuery();
  const [hasSeenPopUp] = useHasSeenPopUpMutation();
  const [achievementType, setAchievementType] = useState<string | undefined>();

  const currentPopup = useMemo(() => {
    return data?.getPopUp?.data?.[0]
  }, [data?.getPopUp?.data])

  useImperativeHandle(ref, () => {
    return {
      refetch
    };
  }, [refetch]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (currentPopup?.popUpType && currentPopup?.popUpDelay) {
      timeout = setTimeout(() => {
        setOpen(true)
      }, currentPopup?.popUpDelay)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [currentPopup, setOpen]);

  const onhandleClose = async () => {
    try {
      if (achievementType) {
        setAchievementType(undefined)
      } else {
        await hasSeenPopUp({
          variables: { popUpType: currentPopup?.popUpType },
          update: (cache) => {
            cache.evict({
              id: cache.identify({
                popUpType: currentPopup?.popUpType,
                "__typename": "popUp"
              })
            })
          }
        });
      }
    } finally {
      setOpen(false);
    }
  };

  const handleOpenModal = (achievementType: string) => () => {
    ReactGA.event(BADGE_STREAK_CLICKED);
    setAchievementType(achievementType);
    setOpen(true);
  }

  const config = popupModalView[achievementType || currentPopup?.popUpType as string];

  return (
    <Fragment>
      <BadgeContext.Provider value={{ handleOpenModal }}>
        {children}
      </BadgeContext.Provider>
      <BadgeModal open={open} onhandleClose={onhandleClose} fullScreen={config?.fullScreen}>
        {["PowerUser_WeekCompleted", "PowerUserStreak"].includes(achievementType || currentPopup?.popUpType as string) ? <RunningStrick /> : null}
        {currentPopup?.popUpType === "PowerUser_WeekMissed" ? <MissedStrick data={currentPopup?.data} /> : null}
        {currentPopup?.popUpType === "PowerUser_StreakCompleted" ? <StrickComplete data={currentPopup?.data} /> : null}
      </BadgeModal>
    </Fragment>
  );
})

export const useBadgeContext = () => {
  return useContext(BadgeContext)
};

export default BadgeContext;
